import React, { useEffect, useState } from "react";
import UpdateTemplateForm from "../UpdateTemplateForm/UpdateTemplateForm";
import styles from "./EditFieldTemplate.module.scss";
import { useTheme } from "../../../hooks/useSingleton";
import getOptions from "../../../utils/getOptions";
import TemplateManagerFieldAction from "../TemplateManagerFieldAction/TemplateManagerFieldAction";
import { Button } from "@factorearth/component-library";
import { FiChevronDown, FiChevronUp, FiTrash2, FiEdit3, FiChevronLeft } from "react-icons/fi";
import FormattedActionBar from "components/FormattedActionBar/FormattedActionBar";
import DrawerTutorialButton from "components/DrawerTutorial/DrawerTutorialButton/DrawerTutorialButton";
import { FieldTemplate, User } from "db/definitions";
import { Location } from "hooks/useDrawer";
import useCustomForm from "hooks/useCustomForm";

interface Props {
	fieldTemplate: FieldTemplate;
	fromTable?: boolean;
	handleRearrangeField: (start: number, end: number) => void;
	handleRemoveFieldTemplate: (id: string) => void;
	handleShowDeleteModal: (cb: () => void, docType: string) => void;
	index: number;
	isLast: boolean;
	online: boolean;
	setupDrawer: (location: Location | "") => void;
	updateFieldTemplate: (item: FieldTemplate) => Promise<void>;
	userDoc: User;
}

function EditFieldTemplate(props: Props) {
	const {
		fieldTemplate,
		fromTable,
		handleRearrangeField,
		handleShowDeleteModal,
		index,
		isLast,
		online,
		updateFieldTemplate,
		userDoc,
		handleRemoveFieldTemplate,
		setupDrawer,
	} = props;

	const [editing, setEditing] = useState(false);
	const color = useTheme();

	const { values, handleChange, handleSubmit, handleUpdateMultiplePropsAtOnce } = useCustomForm(fieldTemplate, updateFieldTemplate);

	useEffect(() => {
		async function setupInitialFieldTemplate() {
			if (fieldTemplate.options?.nextToken) {
				const options = await getOptions(fieldTemplate);
				fieldTemplate.options.items = options;
			}
		}
		setupInitialFieldTemplate();
	}, []);

	function toggleEditing() {
		setEditing(!editing);
	}

	function handleRearrange(direction: "UP" | "DOWN") {
		const modifier = direction === "UP" ? -1 : 1;
		const newIndex = index + modifier;
		if (newIndex > -1) {
			handleRearrangeField(index, newIndex);
		} else {
			console.error("something went wrong.  option index is less than 0");
		}
	}

	function deleteFieldTemplate() {
		handleShowDeleteModal(() => handleRemoveFieldTemplate(fieldTemplate.id), fieldTemplate.header);
	}

	return (
		<div style={{ color: color.text.primary }} className={styles.editFields} data-test="tm-edit-field-list-item" id={fieldTemplate.id}>
			{editing ? (
				<div className={fromTable ? styles.editDiv_fromTable : styles.editDiv} style={{ background: color.background.primary }}>
					<div className={fromTable ? styles.top_bar_fromTable : styles.top_bar}>
						<div
							style={{
								color: color.text.primary,
								background: color.background.primary,
								display: "flex",
							}}
							className={styles.allFieldsHeader}>
							<div onClick={toggleEditing} className={styles.header_back} style={{ paddingRight: "7px" }}>
								<FiChevronLeft />
							</div>
							<div className={styles.header_text}>Editing Field: {fieldTemplate.header}</div>
						</div>
						<FormattedActionBar color={color}>
							<div style={{ background: color.background.primary }} className={styles.left_w_space_between}>
								<Button onClick={toggleEditing} colorPalette={color} variant="outlined">
									<span className={styles.react_icon}>
										<FiChevronLeft />
									</span>
									<div data-test={`back-button-fields${fromTable ? "-cell" : ""}`}>Back</div>
								</Button>
							</div>
							<DrawerTutorialButton
								helpColor={color.utils.success}
								location={fromTable ? "template-manager-table-cells" : "template-manager-edit-field"}
								setupDrawer={setupDrawer}
								style={{ border: "0px" }}
							/>
						</FormattedActionBar>
					</div>
					<div className={fromTable ? styles.form_container_table : styles.form_container}>
						<div className={styles.edit_container}>
							<UpdateTemplateForm
								fromTable={fromTable}
								template={fieldTemplate}
								submitCallback={updateFieldTemplate}
								handleShowDeleteModal={handleShowDeleteModal}
								online={online}
								docType={"FieldTemplate"}
								userDoc={userDoc}
								setupDrawer={setupDrawer}
								values={values}
								handleChange={handleChange}
								handleSubmit={handleSubmit}
								handleUpdateMultiplePropsAtOnce={handleUpdateMultiplePropsAtOnce}
							/>
						</div>
					</div>
				</div>
			) : (
				<div style={{ borderBottom: `1px dotted ${color.background.secondary}` }} className={styles.field_header}>
					<div className={styles.field_item_btn_wrapper}>
						<div
							id={fieldTemplate.id}
							data-test={`edit-${fieldTemplate.id}`}
							style={{ color: color.text.tertiary, background: "transparent", border: `1px solid ${color.border.primary}` }}
							className={styles.button}
							onClick={toggleEditing}>
							<FiEdit3 />
							Edit
						</div>
						<div style={{ display: "flex" }}>
							<Button
								colorPalette={color}
								onClick={() => handleRearrange("UP")}
								variant="outlined"
								disabled={index === 0}
								style={{
									background: index !== 0 ? color.background.primary : color.background.secondary,
									border: `1px solid ${color.border.primary}`,
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									borderRadius: "4px 0px 0px 4px",
									padding: "17px 12px",
									color: color.utils.icon,
								}}>
								<FiChevronUp />
							</Button>
							<Button
								style={{
									background: !isLast ? color.background.primary : color.background.secondary,
									border: `1px solid ${color.border.primary}`,
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									borderRadius: "0px 4px 4px 0px",
									padding: "17px 12px",
									color: color.utils.icon,
									borderLeft: "none",
								}}
								disabled={isLast}
								colorPalette={color}
								onClick={() => handleRearrange("DOWN")}
								variant="outlined">
								<FiChevronDown />
							</Button>
						</div>
					</div>
					<div className={styles.field_preview_wrapper}>
						<TemplateManagerFieldAction
							fieldInstance={{ value: "" }}
							fieldTemplate={fieldTemplate}
							updateObj={{}}
							fromTable={false}
							online={online}
							userDoc={userDoc}
							formInstance={{}}
						/>
					</div>
					<Button variant="destructive" icon={<FiTrash2 />} colorPalette={color} label={"Remove"} onClick={deleteFieldTemplate} />
				</div>
			)}
		</div>
	);
}

export default EditFieldTemplate;
